import { searchMatches } from "@majourneys/shared";
// The Stays search: a slim pewter pill that opens a small floating popup with
// three steps, Where to? / When's your trip? / Who's coming?. Picking a city
// moves to dates, a full range moves to travelers. The caller decides where
// Search goes; on the hotel-type pages that is the same page with the LiteAPI
// pricing params set, so the list comes back priced for those dates.
//
// Child ages are asked here, one per child, because the supplier prices on
// each age (see stay-search-form.tsx). Steppers, not a dropdown: a menu inside
// a clipped popup is cut off at the popup's edge.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { MAX_STAY_CHILDREN, MAX_STAY_CHILD_AGE } from "@majourneys/shared";
import { BookingSkinScope, StayRangePicker } from "@majourneys/booking";
import { pewterFrame, pewterInner, solid } from "../../../shared/ui/pewter-edge";
import { childAgeLabel } from "../lib/child-age-options";

const NAVY = "#110E39";
const META = "#5a5d77";
const MAX_ADULTS = 10;
/** Same as the search form: zero is the cheapest band, so an untouched age
 *  can only understate a party, never sell a teenager at toddler rates. */
const DEFAULT_CHILD_AGE = 0;

const frame = (r: number, gap = 4) => ({
  ...pewterFrame(r, { gap }),
  boxShadow: "0 0 0 1px rgba(255,255,255,0.9), 0 14px 36px -12px rgba(4,0,51,0.22)",
});
const inner = (r: number, gap = 4, fill = "#FAFAFC") => pewterInner(solid(fill), r, { gap });

export interface StaySearchCity {
  slug: string;
  name: string;
  image?: string | null;
}

export interface StaySearchValue {
  city?: string;
  checkin?: string;
  checkout?: string;
  adults: number;
  childAges: number[];
}

type Step = "where" | "when" | "who";

/* Pills on the page, newest last, so the phone map can open the search
   without leaving the map (it sits above the page, the popup above both). */
const openers: Array<(step?: Step) => void> = [];

/**
 * Opens the page's Stays search from elsewhere, at a given step ("when" for
 * the dates prompt) or wherever the pill itself would start. False when no
 * pill is mounted, so the caller can fall back.
 */
export function openStaySearch(step?: Step): boolean {
  const open = openers[openers.length - 1];
  if (!open) return false;
  open(step);
  return true;
}

export function StaySearchPill({
  cities,
  value,
  onSearch,
}: {
  cities: StaySearchCity[];
  value: StaySearchValue;
  onSearch: (next: StaySearchValue) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [step, setStep] = useState<Step>("where");
  const [draft, setDraft] = useState<StaySearchValue>(value);
  const closeTimer = useRef<number | undefined>(undefined);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const fmt = useMemo(() => new Intl.DateTimeFormat(`${lang}-u-nu-latn`, { day: "numeric", month: "short" }), [lang]);
  const day = (iso: string) => fmt.format(new Date(`${iso}T12:00:00Z`));
  const cityName = (slug?: string) =>
    cities.find((c) => c.slug === slug)?.name ?? t("explore.all_of_morocco", { defaultValue: "All of Morocco" });
  const datesLabel = (v: StaySearchValue) =>
    v.checkin && v.checkout ? `${day(v.checkin)} – ${day(v.checkout)}` : null;
  const travelers = (v: StaySearchValue) => t("stays.search.travelers", { count: v.adults + v.childAges.length });

  const openPopup = () => {
    window.clearTimeout(closeTimer.current);
    setDraft(value);
    setStep(value.city ? (value.checkin ? "who" : "when") : "where");
    setOpen(true);
  };
  const openRef = useRef(openPopup);
  openRef.current = openPopup;
  useEffect(() => {
    const fn = (at?: Step) => {
      openRef.current();
      if (at) setStep(at);
    };
    openers.push(fn);
    return () => {
      const i = openers.indexOf(fn);
      if (i >= 0) openers.splice(i, 1);
    };
  }, []);
  const close = () => {
    setShown(false);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      triggerRef.current?.focus();
    }, 220);
  };

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => setShown(true));
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const search = () => {
    // Both ends or neither: half a range cannot be priced.
    const dated = !!(draft.checkin && draft.checkout);
    onSearch({ ...draft, checkin: dated ? draft.checkin : undefined, checkout: dated ? draft.checkout : undefined });
    close();
  };

  const card = (id: Step, title: string, summary: string, body: ReactNode) => {
    const on = step === id;
    return (
      <div
        style={
          on
            ? frame(22)
            : { borderRadius: 18, background: "#fff", boxShadow: "0 0 0 1px rgba(17,14,57,0.08), 0 4px 14px -8px rgba(17,14,57,0.2)" }
        }
      >
        <div style={on ? inner(22) : undefined} className="overflow-hidden rounded-[18px]">
          <button
            type="button"
            onClick={() => setStep(id)}
            aria-expanded={on}
            className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-start"
          >
            <span className={on ? "shrink-0 text-[18px] font-bold" : "shrink-0 text-[14px]"} style={{ color: on ? NAVY : META }}>
              {title}
            </span>
            {!on ? (
              <span className="min-w-0 break-words text-end text-[14px] font-semibold" style={{ color: NAVY }}>
                {summary}
              </span>
            ) : null}
          </button>
          <Reveal open={on}>
            <div className="px-3 pb-3">{body}</div>
          </Reveal>
        </div>
      </div>
    );
  };

  const currentDates = datesLabel(value);

  return (
    <>
      <button ref={triggerRef} type="button" onClick={openPopup} aria-haspopup="dialog" className="w-full sm:max-w-[520px] lg:w-[400px]">
        <span className="block" style={frame(999, 3)}>
          <span className="flex items-center gap-3 py-2 pe-2 ps-4 text-start" style={inner(999, 3)}>
            <Icon name="ri-search-line" className="text-[19px]" style={{ color: NAVY }} aria-hidden="true" />
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block break-words text-[15px] font-bold" style={{ color: NAVY }}>
                {value.city ? cityName(value.city) : t("explore.where_to")}
              </span>
              <span className="block break-words text-[12.5px]" style={{ color: META }}>
                {currentDates ?? t("stays.search.any_dates")} · {travelers(value)}
              </span>
            </span>
            <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: "#EEF0F6", color: NAVY }}>
              <Icon name="ri-equalizer-line" className="text-[17px]" aria-hidden="true" />
            </span>
          </span>
        </span>
      </button>

      {open
        ? createPortal(
            <div className="fixed inset-0 z-[var(--z-modal,9999)]" role="dialog" aria-modal="true" aria-label={t("stays.search.dialog_title")}>
              <div
                className="absolute inset-0 transition-opacity duration-300"
                style={{ background: "rgba(8,6,32,0.42)", backdropFilter: "blur(6px)", opacity: shown ? 1 : 0 }}
                onClick={close}
              />
              {/* A small floating popup, sized to its content, just under the
                  top of the screen. Opacity and transform only. */}
              <div className="pointer-events-none absolute inset-0 flex items-start justify-center px-3 pt-[max(calc(env(safe-area-inset-top)+16px),64px)] sm:pt-[10vh]">
                <div
                  /* Wider while the dates are open so two months sit side by
                     side on a desktop; a phone keeps one. */
                  className={`pointer-events-auto w-full ${step === "when" ? "max-w-[440px] sm:max-w-[720px]" : "max-w-[440px]"} origin-top transition-[opacity,transform] duration-300 ease-[cubic-bezier(.32,.72,0,1)] motion-reduce:transition-none`}
                  style={{
                    ...frame(28),
                    boxShadow: "0 0 0 1px rgba(255,255,255,0.9), 0 30px 80px rgba(4,0,51,0.35)",
                    opacity: shown ? 1 : 0,
                    transform: shown ? "translateY(0) scale(1)" : "translateY(10px) scale(0.96)",
                  }}
                >
                  <div className="flex max-h-[calc(100svh-120px)] flex-col overflow-hidden" style={inner(28, 4, "#F2F3F8")}>
                    <div className="flex items-center justify-between px-4 pb-1 pt-3">
                      <span className="text-[15px] font-bold" style={{ color: NAVY }}>
                        {t("nav.stays")}
                      </span>
                      <button
                        type="button"
                        onClick={close}
                        aria-label={t("common.close")}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-white"
                        style={{ color: NAVY, boxShadow: "0 0 0 1px rgba(17,14,57,0.1)" }}
                      >
                        <Icon name="ri-close-line" className="text-[18px]" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto overscroll-contain px-3 pb-3 pt-1">
                      {card(
                        "where",
                        t("explore.where_to"),
                        cityName(draft.city),
                        <CityList
                          cities={cities}
                          value={draft.city}
                          onPick={(city) => {
                            setDraft((d) => ({ ...d, city }));
                            setStep("when");
                          }}
                        />,
                      )}
                      {card(
                        "when",
                        t("stays.search.when_title"),
                        datesLabel(draft) ?? t("stays.search.add_dates"),
                        <BookingSkinScope skin="pewter">
                          <div className="pt-2">
                            {/* The approved range picker (#53): the range stays on
                                screen with its nights count and "Show hotels",
                                rather than folding away to the guests step the
                                moment the check-out is tapped. The guests step
                                stays one tap away below, with its summary. */}
                            <StayRangePicker
                              checkIn={draft.checkin ?? null}
                              checkOut={draft.checkout ?? null}
                              minNights={1}
                              onChange={(r) =>
                                setDraft((d) => ({ ...d, checkin: r.checkIn ?? undefined, checkout: r.checkOut ?? undefined }))
                              }
                              primaryLabel={t("stays.search.show_hotels")}
                              onPrimary={search}
                            />
                          </div>
                        </BookingSkinScope>,
                      )}
                      {card(
                        "who",
                        t("stays.search.who_title"),
                        travelers(draft),
                        <Travelers value={draft} onChange={(next) => setDraft((d) => ({ ...d, ...next }))} />,
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t bg-white px-4 py-2.5" style={{ borderColor: "rgba(17,14,57,0.08)" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setDraft({ adults: 2, childAges: [] });
                          setStep("where");
                        }}
                        className="min-h-11 px-1 text-[15px] font-semibold underline underline-offset-4"
                        style={{ color: NAVY }}
                      >
                        {t("filter.clear_all")}
                      </button>
                      <button
                        type="button"
                        onClick={search}
                        className="mj-book-cta flex h-12 items-center justify-center gap-2 rounded-full px-6 text-[15px] font-bold text-white"
                        style={{ boxShadow: "0 10px 24px -12px rgba(17,14,57,0.8)" }}
                      >
                        <Icon name="ri-search-line" className="text-[17px]" aria-hidden="true" />
                        {t("stays.search.search")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Grid rows 0fr → 1fr: a smooth open with no measured heights. */
function Reveal({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className="grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(.32,.72,0,1)] motion-reduce:transition-none"
      style={{ gridTemplateRows: open ? "1fr" : "0fr", opacity: open ? 1 : 0 }}
      inert={!open || undefined}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

function CityList({ cities, value, onPick }: { cities: StaySearchCity[]; value?: string; onPick: (slug: string | undefined) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const term = q.trim();
  // "fez" or "fes" finds Fès (2026-11-04).
  const list = cities.filter((c) => searchMatches(term, c.name, c.slug));
  const row = (slug: string | undefined, name: string, image?: string | null) => {
    const on = slug === value;
    return (
      <button
        key={slug ?? "all"}
        type="button"
        onClick={() => onPick(slug)}
        className="flex w-full items-center gap-3 rounded-[14px] px-2 py-1.5 text-start transition-colors hover:bg-white"
        style={on ? { background: "#fff", boxShadow: "0 0 0 1px rgba(17,14,57,0.1)" } : undefined}
      >
        <span className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full" style={{ background: "#EEF0F6", color: NAVY }}>
          {image ? (
            <img src={image.replace(/\/public$/, "/w=96,fit=scale-down,format=auto")} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <Icon name="ri-global-line" className="text-[18px]" aria-hidden="true" />
          )}
        </span>
        <span className="flex-1 text-[14px] font-semibold" style={{ color: NAVY }}>
          {name}
        </span>
        {on ? <Icon name="ri-check-line" className="text-[18px]" style={{ color: NAVY }} aria-hidden="true" /> : null}
      </button>
    );
  };
  return (
    <div className="pt-2">
      <label className="flex h-10 items-center gap-2 rounded-full bg-white px-3.5" style={{ color: META, boxShadow: "0 0 0 1px rgba(17,14,57,0.1)" }}>
        <Icon name="ri-search-line" className="text-[16px]" aria-hidden="true" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("explore.search_city", { defaultValue: "Search a city" })}
          aria-label={t("explore.search_city", { defaultValue: "Search a city" })}
          className="min-w-0 flex-1 bg-transparent text-[16px] outline-none sm:text-[14px]"
          style={{ color: NAVY }}
        />
      </label>
      <div className="mt-2 max-h-[208px] overflow-y-auto overscroll-contain">
        {!term ? row(undefined, t("explore.all_of_morocco", { defaultValue: "All of Morocco" })) : null}
        {list.map((c) => row(c.slug, c.name, c.image))}
        {term && list.length === 0 ? (
          <p className="py-4 text-center text-[13.5px]" style={{ color: META }}>
            {t("explore.no_city_match", { defaultValue: "No city matches that." })}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function Stepper({
  label,
  hint,
  value,
  min,
  max,
  display,
  decLabel,
  incLabel,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  display?: string;
  decLabel: string;
  incLabel: string;
  onChange: (n: number) => void;
}) {
  const btn = (delta: number, icon: string, disabled: boolean, aria: string) => (
    <button
      type="button"
      onClick={() => onChange(value + delta)}
      disabled={disabled}
      aria-label={aria}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-white transition active:scale-90 disabled:opacity-30"
      style={{ color: NAVY, boxShadow: "0 0 0 1px rgba(17,14,57,0.14)" }}
    >
      <Icon name={icon} className="text-[16px]" aria-hidden="true" />
    </button>
  );
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <div className="text-[14.5px] font-semibold" style={{ color: NAVY }}>
          {label}
        </div>
        {hint ? (
          <div className="text-[12.5px]" style={{ color: META }}>
            {hint}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        {btn(-1, "ri-subtract-line", value <= min, decLabel)}
        <span className="min-w-[44px] text-center text-[14.5px] font-semibold" style={{ color: NAVY }} aria-live="polite">
          {display ?? value}
        </span>
        {btn(1, "ri-add-line", value >= max, incLabel)}
      </div>
    </div>
  );
}

export function Travelers({ value, onChange }: { value: StaySearchValue; onChange: (next: Partial<StaySearchValue>) => void }) {
  const { t } = useTranslation();
  const rule = <div className="h-px" style={{ background: "rgba(17,14,57,0.08)" }} />;
  return (
    <div className="px-1 pt-1">
      <Stepper
        label={t("stays.search.adults_label")}
        hint={t("stays.search.adults_hint")}
        value={value.adults}
        min={1}
        max={MAX_ADULTS}
        decLabel={t("stays.search.adults_decrease")}
        incLabel={t("stays.search.adults_increase")}
        onChange={(adults) => onChange({ adults })}
      />
      {rule}
      <Stepper
        label={t("stays.detail.sidebar.children_label")}
        hint={t("stays.search.children_hint")}
        value={value.childAges.length}
        min={0}
        max={MAX_STAY_CHILDREN}
        decLabel={t("stays.detail.sidebar.children_decrease")}
        incLabel={t("stays.detail.sidebar.children_increase")}
        onChange={(n) =>
          onChange({
            childAges:
              n > value.childAges.length ? [...value.childAges, DEFAULT_CHILD_AGE] : value.childAges.slice(0, n),
          })
        }
      />
      {value.childAges.length > 0 ? (
        <>
          {rule}
          <p className="m-0 pt-2.5 text-[12.5px]" style={{ color: META }}>
            {t("stays.detail.sidebar.children_age_hint")}
          </p>
          {value.childAges.map((age, i) => (
            <Stepper
              // eslint-disable-next-line react/no-array-index-key -- ages are positional; there is no id
              key={i}
              label={t("stays.detail.sidebar.child_age_label", { index: i + 1 })}
              value={age}
              min={0}
              max={MAX_STAY_CHILD_AGE}
              display={childAgeLabel(t, age)}
              decLabel={t("stays.search.age_decrease", { index: i + 1 })}
              incLabel={t("stays.search.age_increase", { index: i + 1 })}
              onChange={(next) => onChange({ childAges: value.childAges.map((a, j) => (j === i ? next : a)) })}
            />
          ))}
        </>
      ) : null}
    </div>
  );
}
